import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CheckSquare, MoreHorizontal, Edit, Trash2, Archive, FolderOpen } from 'lucide-react';
import { Project, Task } from '@/types/product-manager';
import { Link } from 'react-router-dom';
import { updateProject, deleteProject, getTasks } from '@/services/productManagerService';
import { useAuth } from '@/context/AuthContext';
import EditProjectDialog from './EditProjectDialog';
import ConfirmDialog from './ConfirmDialog';

interface TaskMetrics {
  todo: number;
  inProgress: number;
  completed: number;
}

interface CustomProjectCardProps {
  project: Project;
  viewMode: 'grid' | 'list';
  onUpdate: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const CustomProjectCard: React.FC<CustomProjectCardProps> = ({ project, viewMode, onUpdate, onEdit, onDelete }) => {
  const [loading, setLoading] = useState(false);
  const [taskMetrics, setTaskMetrics] = useState<TaskMetrics | null>(null);

  // Load task metrics for this project
  useEffect(() => {
    const loadTaskMetrics = async () => {
      try {
        const tasks = await getTasks(project.id);
        const metrics: TaskMetrics = {
          todo: tasks.filter(task => task.status === 'todo').length,
          inProgress: tasks.filter(task => task.status === 'in-progress').length,
          completed: tasks.filter(task => task.status === 'completed').length
        };
        setTaskMetrics(metrics);
      } catch (error) {
        console.error('Error loading task metrics:', error);
        setTaskMetrics(null);
      }
    };

    loadTaskMetrics();
  }, [project.id]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'completed' | 'archived') => {
    try {
      setLoading(true);
      await updateProject(project.id, { 
        status: newStatus,
        updatedAt: new Date() as any
      });
      onUpdate();
    } catch (error) {
      console.error('Error updating project status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    onEdit?.();
  };

  if (viewMode === 'list') {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <Link to={`/product-manager/projects/${project.id}`} className="p-2 rounded-lg bg-violet-500 hover:bg-violet-600 transition-colors cursor-pointer">
                <FolderOpen className="h-6 w-6 text-white" />
              </Link>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {project.name}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2 mr-4">
                    <Badge className={getPriorityColor(project.metadata.priority)}>
                      {project.metadata.priority}
                    </Badge>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                  </div>
                </div>
                
                {/* Task Metrics - Always reserve space */}
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 min-h-[20px]">
                  {taskMetrics && (taskMetrics.todo > 0 || taskMetrics.inProgress > 0 || taskMetrics.completed > 0) && (
                    <>To-Do ({taskMetrics.todo}) In Progress ({taskMetrics.inProgress}) Completed ({taskMetrics.completed})</>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/product-manager/projects/${project.id}`}>
                  <CheckSquare className="h-4 w-4 mr-2" />
                  View Tasks
                </Link>
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" disabled={loading}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleStatusChange('active')}>
                    Mark as Active
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('completed')}>
                    Mark as Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange('archived')}>
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onDelete} className="text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <Link to={`/product-manager/projects/${project.id}`} className="p-2 rounded-lg bg-violet-500 hover:bg-violet-600 transition-colors cursor-pointer">
              <FolderOpen className="h-6 w-6 text-white" />
            </Link>
            <div>
              <CardTitle className="text-lg">{project.name}</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Badge className={getPriorityColor(project.metadata.priority)}>
              {project.metadata.priority}
            </Badge>
            <Badge className={getStatusColor(project.status)}>
              {project.status}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={loading}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleStatusChange('active')}>
                  Mark as Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('completed')}>
                  Mark as Completed
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStatusChange('archived')}>
                  <Archive className="h-4 w-4 mr-2" />
                  Archive
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDelete} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Task Metrics - Always reserve space */}
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 min-h-[20px]">
          {taskMetrics && (taskMetrics.todo > 0 || taskMetrics.inProgress > 0 || taskMetrics.completed > 0) && (
            <>To-Do ({taskMetrics.todo}) In Progress ({taskMetrics.inProgress}) Completed ({taskMetrics.completed})</>
          )}
        </div>
        
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="flex-1" asChild>
            <Link to={`/product-manager/projects/${project.id}`}>
              <CheckSquare className="h-4 w-4 mr-2" />
              View Tasks
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomProjectCardWithDialogs: React.FC<Omit<CustomProjectCardProps, 'onEdit' | 'onDelete'>> = (props) => {
  const { userEmail } = useAuth();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [, setLoading] = useState(false);

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await deleteProject(props.project.id, userEmail || undefined);
      props.onUpdate();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectUpdated = () => {
    props.onUpdate();
    setShowEditDialog(false);
  };

  return (
    <>
      <CustomProjectCard
        {...props}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      
      {/* Edit Project Dialog */}
      <EditProjectDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onProjectUpdated={handleProjectUpdated}
        project={props.project}
      />
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        description="Are you sure you want to delete this project? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
      />
    </>
  );
};

export default CustomProjectCardWithDialogs;